import React from 'react';
import { InPlaceEditorComponent } from '@syncfusion/ej2-react-inplace-editor';
import { FixedSizeList, ListChildComponentProps, ListOnItemsRenderedProps } from 'react-window';
import { MediaGalleryRow } from './MediaGalleryGrid';
import ComponentHelpers from '../../helpers/ComponentHelpers';
import {
    MEDIA_GALLERY_CARD_ZOOM_DEFAULT_PERCENT,
    MEDIA_GALLERY_CARD_ZOOM_MAX_PERCENT,
    MEDIA_GALLERY_CARD_ZOOM_MIN_PERCENT,
    MEDIA_GALLERY_CARD_ZOOM_SESSION_KEY,
    MEDIA_GALLERY_CARD_ZOOM_STEP_PERCENT,
    loadMediaGalleryCardZoomPercent,
    saveMediaGalleryCardZoomPercent,
} from './mediaGalleryCardZoomSession';

const MEDIA_GALLERY_VIRTUALIZATION_THRESHOLD = 100;
const MEDIA_GALLERY_VIRTUAL_OVERSCAN_ROWS = 2;
const MEDIA_GALLERY_THUMBNAIL_PREFETCH_MARGIN = '400px 0px';
const MEDIA_GALLERY_CARD_BASE_WIDTH = 205;
const MEDIA_GALLERY_CARD_BASE_GAP = 16;
const MEDIA_GALLERY_CARD_EXTRA_HEIGHT = 90;
const MEDIA_GALLERY_SCROLLBAR_WIDTH_RESERVE = 18;
const MEDIA_GALLERY_VIRTUAL_LIST_MIN_HEIGHT = 160;

interface MediaGalleryThumbnailProps {
    rows: MediaGalleryRow[];
    selectedMediaIdSet: ReadonlySet<string>;
    selectionToolbar?: React.ReactNode;
    onOpenView: (view: IView360Detail) => void;
    onSelectView: (view: IView360Detail, mediaKind?: 'scanPositions' | 'image360') => void;
    onToggleMediaSelection: (mediaId: string) => void;
    onUpdateViewMetadata: (update: IView360MetadataUpdate, mediaKind?: 'scanPositions' | 'image360') => Promise<void>;
    canModifyMediaFiles: boolean;
}

type ObserveThumbnail = (element: HTMLElement, onVisible: () => void) => () => void;

interface MediaGalleryThumbnailCardProps {
    row: MediaGalleryRow;
    isRevealed: boolean;
    isSelected: boolean;
    canSelect: boolean;
    observeThumbnail: ObserveThumbnail;
    requestedThumbnailUrlSet: React.MutableRefObject<Set<string>>;
    onShowReveal: (row: MediaGalleryRow, event: React.MouseEvent<HTMLElement>) => void;
    onThumbnailKeyDown: (row: MediaGalleryRow, event: React.KeyboardEvent<HTMLDivElement>) => void;
    onHideReveal: (cardId: string, event: React.MouseEvent<HTMLButtonElement>) => void;
    onToggleSelection: (row: MediaGalleryRow, event: React.MouseEvent<HTMLButtonElement>) => void;
    onOpenView: (view: IView360Detail) => void;
    onSelectView: (view: IView360Detail, mediaKind?: 'scanPositions' | 'image360') => void;
    renderMetadataValue: (row: MediaGalleryRow, field: keyof IView360MetadataUpdate, value: number | null, digits: number) => React.ReactNode;
}

interface VirtualRowData {
    rows: MediaGalleryRow[];
    columnCount: number;
    gap: number;
    selectedMediaIdSet: ReadonlySet<string>;
    revealedCardId: string | null;
    canSelect: boolean;
    observeThumbnail: ObserveThumbnail;
    requestedThumbnailUrlSet: React.MutableRefObject<Set<string>>;
    onShowReveal: MediaGalleryThumbnailCardProps['onShowReveal'];
    onThumbnailKeyDown: MediaGalleryThumbnailCardProps['onThumbnailKeyDown'];
    onHideReveal: MediaGalleryThumbnailCardProps['onHideReveal'];
    onToggleSelection: MediaGalleryThumbnailCardProps['onToggleSelection'];
    onOpenView: MediaGalleryThumbnailCardProps['onOpenView'];
    onSelectView: MediaGalleryThumbnailCardProps['onSelectView'];
    renderMetadataValue: MediaGalleryThumbnailCardProps['renderMetadataValue'];
}

const useThumbnailObserver = (root: HTMLElement | null): ObserveThumbnail => {
    const observerRef = React.useRef<IntersectionObserver | null>(null);
    const callbackMapRef = React.useRef(new Map<HTMLElement, () => void>());

    React.useEffect(() => {
        observerRef.current?.disconnect();

        if (typeof IntersectionObserver === 'undefined') {
            callbackMapRef.current.forEach(callback => callback());
            return undefined;
        }

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) {
                    return;
                }

                const element = entry.target as HTMLElement;
                const onVisible = callbackMapRef.current.get(element);
                if (onVisible) {
                    onVisible();
                    callbackMapRef.current.delete(element);
                    observer.unobserve(element);
                }
            });
        }, {
            root,
            rootMargin: MEDIA_GALLERY_THUMBNAIL_PREFETCH_MARGIN,
        });

        observerRef.current = observer;
        callbackMapRef.current.forEach((_callback, element) => observer.observe(element));

        return () => {
            observer.disconnect();
            if (observerRef.current === observer) {
                observerRef.current = null;
            }
        };
    }, [root]);

    return React.useCallback((element: HTMLElement, onVisible: () => void) => {
        callbackMapRef.current.set(element, onVisible);

        if (typeof IntersectionObserver === 'undefined') {
            onVisible();
            callbackMapRef.current.delete(element);
        } else {
            observerRef.current?.observe(element);
        }

        return () => {
            callbackMapRef.current.delete(element);
            observerRef.current?.unobserve(element);
        };
    }, []);
};

interface LazyThumbnailImageProps {
    url: string;
    alt: string;
    observeThumbnail: ObserveThumbnail;
    requestedThumbnailUrlSet: React.MutableRefObject<Set<string>>;
}

const LazyThumbnailImage = React.memo<LazyThumbnailImageProps>(({ url, alt, observeThumbnail, requestedThumbnailUrlSet }) => {
    const [shouldLoad, setShouldLoad] = React.useState(() => !url || requestedThumbnailUrlSet.current.has(url));
    const cleanupRef = React.useRef<(() => void) | null>(null);

    React.useEffect(() => {
        setShouldLoad(!url || requestedThumbnailUrlSet.current.has(url));
    }, [requestedThumbnailUrlSet, url]);

    const setPlaceholderRef = React.useCallback((element: HTMLDivElement | null) => {
        cleanupRef.current?.();
        cleanupRef.current = null;

        if (!element || shouldLoad || !url) {
            return;
        }

        cleanupRef.current = observeThumbnail(element, () => {
            requestedThumbnailUrlSet.current.add(url);
            setShouldLoad(true);
        });
    }, [observeThumbnail, requestedThumbnailUrlSet, shouldLoad, url]);

    React.useEffect(() => () => cleanupRef.current?.(), []);

    return (
        <div ref={setPlaceholderRef} className="media-gallery-card-thumbnail-loader">
            {shouldLoad && url && (
                <img
                    className="media-gallery-card-image"
                    src={url}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                />
            )}
        </div>
    );
});

const MediaGalleryThumbnailCard = React.memo<MediaGalleryThumbnailCardProps>(({
    row,
    isRevealed,
    isSelected,
    canSelect,
    observeThumbnail,
    requestedThumbnailUrlSet,
    onShowReveal,
    onThumbnailKeyDown,
    onHideReveal,
    onToggleSelection,
    onOpenView,
    onSelectView,
    renderMetadataValue,
}) => {
    const iconClass = row.MediaKind === 'scanPositions'
        ? 'media-gallery-type-icon media-gallery-type-icon-scan'
        : 'media-gallery-type-icon media-gallery-type-icon-image';

    return (
        <div className="media-gallery-card-layout" data-media-id={row.ID}>
            <div className={isRevealed ? `e-card media-gallery-card is-revealed${isSelected ? ' is-selected' : ''}` : `e-card media-gallery-card${isSelected ? ' is-selected' : ''}`} title={row.Name}>
                <div className={isRevealed ? 'media-gallery-card-details-shell is-revealed' : 'media-gallery-card-details-shell'}>
                    <div
                        className={isRevealed ? 'media-gallery-card-face is-revealed' : 'media-gallery-card-face'}
                        role="button"
                        tabIndex={0}
                        aria-label={`Show details for ${row.Name}`}
                        onClick={(event) => onShowReveal(row, event)}
                        onKeyDown={(event) => onThumbnailKeyDown(row, event)}
                    >
                        <div className="media-gallery-card-placeholder">
                            {canSelect && <button
                                type="button"
                                className={isSelected ? 'media-gallery-card-selection-toggle is-selected' : 'media-gallery-card-selection-toggle'}
                                title={isSelected ? InovxLoader.t('facility:mediaGallery.removeFromSelection') : InovxLoader.t('facility:mediaGallery.addToSelection')}
                                aria-label={isSelected ? InovxLoader.t('facility:mediaGallery.removeFromSelection') : InovxLoader.t('facility:mediaGallery.addToSelection')}
                                onClick={(event) => onToggleSelection(row, event)}
                            >
                                <span className="fa fa-check" aria-hidden="true"></span>
                            </button>}
                            <LazyThumbnailImage
                                url={row.ThumbnailImageUrl}
                                alt={row.Name}
                                observeThumbnail={observeThumbnail}
                                requestedThumbnailUrlSet={requestedThumbnailUrlSet}
                            />
                        </div>
                        <div className="media-gallery-card-title-row">
                            <span className={iconClass} aria-hidden="true"></span>
                            <div className="media-gallery-card-title">{row.Name}</div>
                        </div>

                        <div className="media-gallery-card-actions-row">
                            <div className="e-card-actions knowledge-library-card-primary-actions">
                                <button
                                    type="button"
                                    className="e-btn e-outline e-primary knowledge-library-card-open-button media-gallery-card-view-button"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onSelectView(row.View, row.MediaKind);
                                        onOpenView(row.View);
                                    }}
                                >
                                    {InovxLoader.t('facility:mediaGallery.view')}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={isRevealed ? 'media-gallery-card-reveal-face is-revealed' : 'media-gallery-card-reveal-face'}>
                        <div className="e-card-header media-gallery-card-reveal-header-shell">
                            <div className="e-card-header-caption direction-row">
                                <span className={iconClass} aria-hidden="true"></span>
                                <div className="e-card-header-title media-gallery-card-reveal-title">{row.Name}</div>
                            </div>
                            <button
                                type="button"
                                className="media-gallery-card-collapse"
                                title={InovxLoader.t('facility:knowledgeBook.closeDetails')}
                                onClick={(event) => onHideReveal(row.ID, event)}
                            >
                                <span className="e-icons e-close" aria-hidden="true"></span>
                            </button>
                        </div>
                        <div className="e-card-content media-gallery-card-reveal-content">
                            <div className="e-card-header-title font-bold text-black mb-5">{InovxLoader.t('facility:mediaGallery.positionAndOrientation')}</div>
                            <table>
                                <tbody>
                                    <tr>
                                        <td>{InovxLoader.t('facility:mediaGallery.north')}</td>
                                        <td>{renderMetadataValue(row, 'North', row.North, 3)}</td>
                                    </tr>
                                    <tr>
                                        <td>{InovxLoader.t('facility:mediaGallery.east')}</td>
                                        <td>{renderMetadataValue(row, 'East', row.East, 3)}</td>
                                    </tr>
                                    <tr>
                                        <td>{InovxLoader.t('facility:mediaGallery.up')}</td>
                                        <td>{renderMetadataValue(row, 'Up', row.Up, 3)}</td>
                                    </tr>
                                    <tr>
                                        <td>{InovxLoader.t('facility:mediaGallery.angle')}</td>
                                        <td>{renderMetadataValue(row, 'Angle', row.Angle, 1)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

const MediaGalleryVirtualRow = React.memo(({ index, style, data }: ListChildComponentProps<VirtualRowData>) => {
    const startIndex = index * data.columnCount;
    const rowItems = data.rows.slice(startIndex, startIndex + data.columnCount);

    return (
        <div
            style={{
                ...style,
                display: 'flex',
                gap: data.gap,
            }}
            className="media-gallery-virtual-row"
        >
            {rowItems.map(row => (
                <MediaGalleryThumbnailCard
                    key={row.ID}
                    row={row}
                    isRevealed={data.revealedCardId === row.ID}
                    isSelected={data.selectedMediaIdSet.has(row.ID)}
                    canSelect={data.canSelect}
                    observeThumbnail={data.observeThumbnail}
                    requestedThumbnailUrlSet={data.requestedThumbnailUrlSet}
                    onShowReveal={data.onShowReveal}
                    onThumbnailKeyDown={data.onThumbnailKeyDown}
                    onHideReveal={data.onHideReveal}
                    onToggleSelection={data.onToggleSelection}
                    onOpenView={data.onOpenView}
                    onSelectView={data.onSelectView}
                    renderMetadataValue={data.renderMetadataValue}
                />
            ))}
        </div>
    );
});

const MediaGalleryThumbnail: React.FC<MediaGalleryThumbnailProps> = ({ rows, selectedMediaIdSet, selectionToolbar, onOpenView, onSelectView, onToggleMediaSelection, onUpdateViewMetadata, canModifyMediaFiles }) => {
    const [revealedCardId, setRevealedCardId] = React.useState<string | null>(null);
    const [cardZoomPercent, setCardZoomPercent] = React.useState<number>(() => loadMediaGalleryCardZoomPercent(MEDIA_GALLERY_CARD_ZOOM_SESSION_KEY));
    const [contentWidth, setContentWidth] = React.useState(0);
    const [virtualListHeight, setVirtualListHeight] = React.useState(MEDIA_GALLERY_VIRTUAL_LIST_MIN_HEIGHT);
    const [thumbnailObserverRoot, setThumbnailObserverRoot] = React.useState<HTMLElement | null>(null);
    const shellRef = React.useRef<HTMLDivElement | null>(null);
    const toolbarRef = React.useRef<HTMLDivElement | null>(null);
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const virtualListRef = React.useRef<FixedSizeList<VirtualRowData> | null>(null);
    const virtualScrollElementRef = React.useRef<HTMLElement | null>(null);
    const editorRefs = React.useRef<Record<string, InPlaceEditorComponent | null>>({});
    const requestedThumbnailUrlSet = React.useRef(new Set<string>());
    const firstVisibleMediaIdRef = React.useRef<string | null>(rows[0]?.ID ?? null);
    const pendingLayoutAnchorIdRef = React.useRef<string | null>(null);
    const lastMeasuredWidthRef = React.useRef(0);
    const previousRowsSignatureRef = React.useRef('');
    const wasVirtualizedRef = React.useRef(false);
    const cardGridClassName = rows.length < 5
        ? 'media-gallery-card-grid media-gallery-card-grid-compact'
        : 'media-gallery-card-grid';
    const cardScale = cardZoomPercent / 100;
    const cardWidth = MEDIA_GALLERY_CARD_BASE_WIDTH * cardScale;
    const cardHeight = (cardWidth * 9 / 16) + MEDIA_GALLERY_CARD_EXTRA_HEIGHT;
    const cardGap = MEDIA_GALLERY_CARD_BASE_GAP * cardScale;
    const shouldVirtualize = rows.length > MEDIA_GALLERY_VIRTUALIZATION_THRESHOLD;
    const availableCardWidth = Math.max(0, contentWidth - (shouldVirtualize ? MEDIA_GALLERY_SCROLLBAR_WIDTH_RESERVE : 0));
    const columnCount = Math.max(1, Math.floor((availableCardWidth + cardGap) / (cardWidth + cardGap)));
    const virtualRowCount = Math.ceil(rows.length / columnCount);
    const virtualRowHeight = cardHeight + cardGap;
    const rowsSignature = React.useMemo(() => rows.map(row => row.ID).join('|'), [rows]);

    const hasModifyMediaFilesPermission = React.useMemo(() => ComponentHelpers.hasModifyMediaFilesPermission(), []);
    const hasManageMediaFilesPermission = React.useMemo(() => ComponentHelpers.hasManageMediaFilesPermission(), []);
    const canSelect = hasModifyMediaFilesPermission || hasManageMediaFilesPermission;

    const saveActiveEditors = React.useCallback(() => {
        Object.values(editorRefs.current).forEach(editor => {
            if (editor?.enableEditMode) {
                editor.save();
            }
        });
    }, []);

    const captureVisibleAnchor = React.useCallback(() => {
        pendingLayoutAnchorIdRef.current = firstVisibleMediaIdRef.current;
    }, []);

    React.useLayoutEffect(() => {
        const updateMeasurements = () => {
            const nextWidth = Math.floor(contentRef.current?.getBoundingClientRect().width ?? 0);
            if (nextWidth > 0 && nextWidth !== lastMeasuredWidthRef.current) {
                pendingLayoutAnchorIdRef.current = firstVisibleMediaIdRef.current;
                lastMeasuredWidthRef.current = nextWidth;
                saveActiveEditors();
                setContentWidth(nextWidth);
            }

            const toolbarHeight = toolbarRef.current?.getBoundingClientRect().height ?? 0;
            const availableHeight = Math.floor((window.innerHeight * 0.7) - toolbarHeight - 10);
            setVirtualListHeight(Math.max(MEDIA_GALLERY_VIRTUAL_LIST_MIN_HEIGHT, availableHeight));
        };

        updateMeasurements();
        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateMeasurements);
        if (contentRef.current) {
            resizeObserver?.observe(contentRef.current);
        }
        if (toolbarRef.current) {
            resizeObserver?.observe(toolbarRef.current);
        }
        window.addEventListener('resize', updateMeasurements);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateMeasurements);
        };
    }, [saveActiveEditors]);

    React.useEffect(() => {
        const scrollElement = virtualScrollElementRef.current;
        if (!scrollElement) {
            return undefined;
        }

        scrollElement.addEventListener('scroll', saveActiveEditors);
        return () => scrollElement.removeEventListener('scroll', saveActiveEditors);
    }, [saveActiveEditors, thumbnailObserverRoot]);

    React.useLayoutEffect(() => {
        const rowsChanged = previousRowsSignatureRef.current !== rowsSignature;
        const becameVirtualized = shouldVirtualize && !wasVirtualizedRef.current;
        previousRowsSignatureRef.current = rowsSignature;
        wasVirtualizedRef.current = shouldVirtualize;

        if (rowsChanged) {
            saveActiveEditors();
            firstVisibleMediaIdRef.current = rows[0]?.ID ?? null;
            pendingLayoutAnchorIdRef.current = null;
            shellRef.current?.scrollTo({ top: 0 });
            if (shouldVirtualize) {
                virtualListRef.current?.scrollToItem(0, 'start');
            }
            return;
        }

        if (!shouldVirtualize) {
            pendingLayoutAnchorIdRef.current = null;
            return;
        }

        const anchorId = pendingLayoutAnchorIdRef.current;
        pendingLayoutAnchorIdRef.current = null;
        if (becameVirtualized || !anchorId) {
            if (becameVirtualized) {
                virtualListRef.current?.scrollToItem(0, 'start');
            }
            return;
        }

        const anchorIndex = rows.findIndex(row => row.ID === anchorId);
        if (anchorIndex >= 0) {
            saveActiveEditors();
            virtualListRef.current?.scrollToItem(Math.floor(anchorIndex / columnCount), 'start');
        }
    }, [cardZoomPercent, columnCount, contentWidth, rows, rowsSignature, saveActiveEditors, shouldVirtualize]);

    React.useEffect(() => {
        const visibleRowIdSet = new Set(rows.map(row => row.ID));
        setRevealedCardId(current => (current && !visibleRowIdSet.has(current) ? null : current));
    }, [rows]);

    const setShellElement = React.useCallback((element: HTMLDivElement | null) => {
        shellRef.current = element;
        if (!shouldVirtualize) {
            setThumbnailObserverRoot(element);
        }
    }, [shouldVirtualize]);

    const setVirtualScrollElement = React.useCallback((element: HTMLElement | null) => {
        virtualScrollElementRef.current = element;
        if (shouldVirtualize) {
            setThumbnailObserverRoot(element);
        }
    }, [shouldVirtualize]);

    React.useEffect(() => {
        setThumbnailObserverRoot(shouldVirtualize ? virtualScrollElementRef.current : shellRef.current);
    }, [shouldVirtualize]);

    const observeThumbnail = useThumbnailObserver(thumbnailObserverRoot);

    const handleZoomChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        captureVisibleAnchor();
        saveActiveEditors();
        setCardZoomPercent(saveMediaGalleryCardZoomPercent(MEDIA_GALLERY_CARD_ZOOM_SESSION_KEY, Number(event.target.value)));
    }, [captureVisibleAnchor, saveActiveEditors]);

    const handleZoomStep = React.useCallback((delta: number) => {
        captureVisibleAnchor();
        saveActiveEditors();
        setCardZoomPercent(previous => saveMediaGalleryCardZoomPercent(MEDIA_GALLERY_CARD_ZOOM_SESSION_KEY, previous + delta));
    }, [captureVisibleAnchor, saveActiveEditors]);

    const handleZoomReset = React.useCallback(() => {
        captureVisibleAnchor();
        saveActiveEditors();
        setCardZoomPercent(saveMediaGalleryCardZoomPercent(MEDIA_GALLERY_CARD_ZOOM_SESSION_KEY, MEDIA_GALLERY_CARD_ZOOM_DEFAULT_PERCENT));
    }, [captureVisibleAnchor, saveActiveEditors]);

    const showReveal = React.useCallback((row: MediaGalleryRow, event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onSelectView(row.View, row.MediaKind);
        setRevealedCardId(row.ID);
    }, [onSelectView]);

    const handleThumbnailKeyDown = React.useCallback((row: MediaGalleryRow, event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        onSelectView(row.View, row.MediaKind);
        setRevealedCardId(row.ID);
    }, [onSelectView]);

    const hideReveal = React.useCallback((cardId: string, event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setRevealedCardId(current => current === cardId ? null : current);
    }, []);

    const toggleCardSelection = React.useCallback((row: MediaGalleryRow, event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onToggleMediaSelection(row.ID);
    }, [onToggleMediaSelection]);

    const updateMetadata = React.useCallback(async (row: MediaGalleryRow, field: keyof IView360MetadataUpdate, value: number | null) => {
        const clearAngle = field === 'Angle' && value == null;
        await onUpdateViewMetadata({
            ID: row.View.ID,
            VersionToken: row.View.VersionToken,
            [field]: value,
            ClearAngle: clearAngle || undefined
        }, row.MediaKind);
    }, [onUpdateViewMetadata]);

    const renderMetadataValue = React.useCallback((row: MediaGalleryRow, field: keyof IView360MetadataUpdate, value: number | null, digits: number) => {
        if (row.MediaKind !== 'image360' || !canModifyMediaFiles) {
            return value == null ? 'N/A' : value.toFixed(digits);
        }

        const editorKey = `${row.ID}-${field}`;
        return (
            <InPlaceNumericEditor
                value={value}
                editable={true}
                allowZero={true}
                editorKey={editorKey}
                editorRefs={editorRefs}
                onChange={async (nextValue) => {
                    await updateMetadata(row, field, nextValue);
                }}
            />
        );
    }, [canModifyMediaFiles, updateMetadata]);

    const handleItemsRendered = React.useCallback(({ visibleStartIndex }: ListOnItemsRenderedProps) => {
        if (!pendingLayoutAnchorIdRef.current) {
            firstVisibleMediaIdRef.current = rows[visibleStartIndex * columnCount]?.ID ?? null;
        }
    }, [columnCount, rows]);

    const virtualRowData = React.useMemo<VirtualRowData>(() => ({
        rows,
        columnCount,
        gap: cardGap,
        selectedMediaIdSet,
        revealedCardId,
        canSelect,
        observeThumbnail,
        requestedThumbnailUrlSet,
        onShowReveal: showReveal,
        onThumbnailKeyDown: handleThumbnailKeyDown,
        onHideReveal: hideReveal,
        onToggleSelection: toggleCardSelection,
        onOpenView,
        onSelectView,
        renderMetadataValue,
    }), [canSelect, cardGap, columnCount, handleThumbnailKeyDown, hideReveal, observeThumbnail, onOpenView, onSelectView, renderMetadataValue, revealedCardId, rows, selectedMediaIdSet, showReveal, toggleCardSelection]);

    const virtualItemKey = React.useCallback((index: number, data: VirtualRowData) => {
        return data.rows[index * data.columnCount]?.ID ?? index;
    }, []);

    const renderCard = React.useCallback((row: MediaGalleryRow) => (
        <MediaGalleryThumbnailCard
            key={row.ID}
            row={row}
            isRevealed={revealedCardId === row.ID}
            isSelected={selectedMediaIdSet.has(row.ID)}
            canSelect={canSelect}
            observeThumbnail={observeThumbnail}
            requestedThumbnailUrlSet={requestedThumbnailUrlSet}
            onShowReveal={showReveal}
            onThumbnailKeyDown={handleThumbnailKeyDown}
            onHideReveal={hideReveal}
            onToggleSelection={toggleCardSelection}
            onOpenView={onOpenView}
            onSelectView={onSelectView}
            renderMetadataValue={renderMetadataValue}
        />
    ), [canSelect, handleThumbnailKeyDown, hideReveal, observeThumbnail, onOpenView, onSelectView, renderMetadataValue, revealedCardId, selectedMediaIdSet, showReveal, toggleCardSelection]);

    return (
        <div
            ref={setShellElement}
            className={shouldVirtualize ? 'media-gallery-thumbnail-shell is-virtualized' : 'media-gallery-thumbnail-shell'}
            style={{
                ['--mg-card-scale' as string]: cardScale,
            }}
        >
            <div ref={toolbarRef} className="media-gallery-thumbnail-toolbar-row">
                {selectionToolbar && (
                    <div className="media-gallery-selection-toolbar-row">
                        {selectionToolbar}
                    </div>
                )}
                <div className="knowledge-library-card-zoom" title={InovxLoader.t('facility:knowledgeBook.cardZoom')}>
                    <button
                        type="button"
                        className="knowledge-library-card-zoom-button"
                        title={InovxLoader.t('facility:knowledgeBook.cardZoomOut')}
                        aria-label={InovxLoader.t('facility:knowledgeBook.cardZoomOut')}
                        onClick={() => handleZoomStep(-MEDIA_GALLERY_CARD_ZOOM_STEP_PERCENT)}
                        disabled={cardZoomPercent <= MEDIA_GALLERY_CARD_ZOOM_MIN_PERCENT}
                    >
                        <span className="fa fa-search-minus" aria-hidden="true"></span>
                    </button>
                    <input
                        type="range"
                        className="knowledge-library-card-zoom-slider"
                        min={MEDIA_GALLERY_CARD_ZOOM_MIN_PERCENT}
                        max={MEDIA_GALLERY_CARD_ZOOM_MAX_PERCENT}
                        step={MEDIA_GALLERY_CARD_ZOOM_STEP_PERCENT}
                        value={cardZoomPercent}
                        onChange={handleZoomChange}
                        aria-label={InovxLoader.t('facility:knowledgeBook.cardZoom')}
                    />
                    <button
                        type="button"
                        className="knowledge-library-card-zoom-button"
                        title={InovxLoader.t('facility:knowledgeBook.cardZoomIn')}
                        aria-label={InovxLoader.t('facility:knowledgeBook.cardZoomIn')}
                        onClick={() => handleZoomStep(MEDIA_GALLERY_CARD_ZOOM_STEP_PERCENT)}
                        disabled={cardZoomPercent >= MEDIA_GALLERY_CARD_ZOOM_MAX_PERCENT}
                    >
                        <span className="fa fa-search-plus" aria-hidden="true"></span>
                    </button>
                    <button
                        type="button"
                        className="knowledge-library-card-zoom-value"
                        title={InovxLoader.t('facility:knowledgeBook.cardZoomReset')}
                        aria-label={InovxLoader.t('facility:knowledgeBook.cardZoomReset')}
                        onClick={handleZoomReset}
                    >
                        {`${cardZoomPercent}%`}
                    </button>
                </div>
            </div>
            <div ref={contentRef} className="media-gallery-thumbnail-content">
                {shouldVirtualize ? (
                    contentWidth > 0 && (
                        <FixedSizeList<VirtualRowData>
                            ref={virtualListRef}
                            className="media-gallery-virtual-list"
                            outerRef={setVirtualScrollElement}
                            width={contentWidth}
                            height={virtualListHeight}
                            itemCount={virtualRowCount}
                            itemSize={virtualRowHeight}
                            itemData={virtualRowData}
                            itemKey={virtualItemKey}
                            overscanCount={MEDIA_GALLERY_VIRTUAL_OVERSCAN_ROWS}
                            onItemsRendered={handleItemsRendered}
                        >
                            {MediaGalleryVirtualRow}
                        </FixedSizeList>
                    )
                ) : (
                    <div className={cardGridClassName}>
                        {rows.map(renderCard)}
                    </div>
                )}
            </div>
        </div>
    );
};

interface InPlaceNumericEditorProps {
    value: number | null;
    editable: boolean;
    allowZero: boolean;
    editorKey: string;
    editorRefs: React.MutableRefObject<Record<string, InPlaceEditorComponent | null>>;
    onChange: (value: number | null) => void;
}

const InPlaceNumericEditor: React.FC<InPlaceNumericEditorProps> = ({ value, editable, allowZero, editorKey, editorRefs, onChange }) => (
    <InPlaceEditorComponent
        ref={el => {
            if (el) {
                editorRefs.current[editorKey] = el;
            } else {
                delete editorRefs.current[editorKey];
            }
        }}
        mode="Popup"
        type="Numeric"
        value={value ?? undefined}
        emptyText="N/A"
        showButtons={true}
        submitOnEnter={true}
        editableOn="Click"
        actionOnBlur="Submit"
        cssClass="inplace-numeric-editor"
        model={{ decimals: 10, format: '##########.##########', showSpinButton: false, enabled: editable }}
        actionBegin={(e: any) => {
            const numericValue = Number(e.data?.value);

            if (e.data?.value !== '' && e.data?.value !== null && !allowZero && numericValue === 0) {
                e.cancel = true;
            }
        }}
        actionSuccess={(e: any) => {
            const nextValue = Object.prototype.hasOwnProperty.call(e, 'value') ? e.value : e.data?.value;

            if (nextValue !== undefined) {
                onChange(nextValue === '' || nextValue === null ? null : Number(nextValue));
            }
        }}
        cancelClick={() => {
            const editor = editorRefs.current[editorKey];
            if (editor) {
                editor.value = value;
            }
        }}
        disabled={!editable}
    />
);

export default MediaGalleryThumbnail;
